import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, LogIn, Download, FileText, Eye, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StaffChanges from "./StaffChanges";

const TYPE_META = {
  login: { label: "Signed in", color: "bg-green-600", Icon: LogIn },
  catalog_view: { label: "Viewed catalog", color: "bg-blue-600", Icon: Eye },
  download: { label: "Downloaded file", color: "bg-slate-700", Icon: Download },
  export: { label: "Exported CSV/PDF", color: "bg-amber-600", Icon: FileText },
  portal_link_sent: { label: "Portal link emailed", color: "bg-indigo-600", Icon: Mail },
  price_list_sent: { label: "Price list PDF emailed", color: "bg-teal-600", Icon: FileText },
};

export default function ActivityPanel() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/activity").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const exportActivity = async () => {
    try {
      const r = await api.get("/admin/activity/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "thermaco_activity.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const filtered = filter === "all" ? rows : rows.filter((e) => e.type === filter);
  const FILTERS = [["all", "All events"], ...Object.entries(TYPE_META).map(([k, v]) => [k, v.label])];

  return (
    <div>
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Activity Log</h1>
          <p className="text-slate-500 mt-1">Customer sign-ins, catalog views, downloads, CSV exports, and portal links emailed.</p>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={load} data-testid="refresh-activity">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="activity-filter">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`activity-filter-${key}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${filter === key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            {label}
          </button>
        ))}
        <button onClick={exportActivity} data-testid="export-activity-button" className="ml-auto text-xs font-medium px-3 py-1.5 rounded-sm border bg-white text-slate-700 border-slate-200 hover:border-slate-400 inline-flex items-center gap-1.5">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Event</th>
              <th className="text-left px-4 py-3">Detail</th>
              <th className="text-left px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody data-testid="activity-table">
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No customer activity yet.</td></tr>
            )}
            {filtered.map((e) => {
              const meta = TYPE_META[e.type] || { label: e.type, color: "bg-slate-500", Icon: Eye };
              const Icon = meta.Icon;
              return (
                <tr key={e.id} className="border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors">
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{e.customer_name}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`rounded-sm ${meta.color}`}><Icon className="h-3 w-3 mr-1" />{meta.label}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{e.detail || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{e.ip}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {user?.is_admin && <StaffChanges />}
    </div>
  );
}
